import React, { useState } from 'react';
import { X, Mail, Lock, Send, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import clienteService from '../services/clienteService';

interface SMTPTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  smtpServidor: string;
  smtpPorta: number;
  smtpTls: boolean;
  defaultEmail: string;
}

const SMTPTestModal: React.FC<SMTPTestModalProps> = ({
  isOpen,
  onClose,
  smtpServidor,
  smtpPorta,
  smtpTls,
  defaultEmail,
}) => {
  const [smtpEmail, setSmtpEmail] = useState(defaultEmail);
  const [smtpPassword, setSmtpPassword] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!smtpEmail || !smtpPassword) {
      toast.error('Preencha email e senha SMTP');
      return;
    }

    setIsLoading(true);
    try {
      const result = await clienteService.testSMTP({
        smtp_email: smtpEmail,
        smtp_password: smtpPassword,
        smtp_servidor: smtpServidor,
        smtp_porta: smtpPorta,
        smtp_tls: smtpTls,
        test_recipient: testRecipient || smtpEmail,
      });

      if (result.success) {
        toast.success(result.message || 'Email de teste enviado com sucesso!');
        handleClose();
      } else {
        toast.error(result.message || 'Erro ao enviar email de teste');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao testar SMTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSmtpEmail(defaultEmail);
    setSmtpPassword('');
    setTestRecipient('');
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Testar Configuração SMTP
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* SMTP Server Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Servidor:</strong> {smtpServidor}:{smtpPorta} ({smtpTls ? 'TLS' : 'Sem TLS'})
            </p>
          </div>

          {/* SMTP Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email SMTP <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={smtpEmail}
                onChange={(e) => setSmtpEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="seu-email@gmail.com"
                required
              />
            </div>
          </div>

          {/* SMTP Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Senha SMTP <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Senha do email ou App Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Para Gmail, use uma senha de app (App Password)
            </p>
          </div>

          {/* Test Recipient (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Destinatário do Teste <span className="text-gray-400 text-xs">(opcional)</span>
            </label>
            <div className="relative">
              <Send className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Deixe vazio para enviar para o próprio email"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar Teste
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SMTPTestModal;
